import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Copy, Check, Smartphone, ChevronDown, ChevronUp, Radio, Satellite, Unlink } from 'lucide-react';
import { toast } from 'sonner';

const SERVER_URL = `${process.env.REACT_APP_BACKEND_URL}/api/tracker/osmand`;

function CopyField({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate rounded-md bg-gray-100 border border-gray-200 px-3 py-2.5 text-sm text-orange-700 font-mono" data-testid={testId}>
          {value}
        </code>
        <Button size="icon" variant="outline" onClick={copy} className="h-11 w-11 border-gray-300 bg-white hover:bg-gray-100 text-gray-800 shrink-0" data-testid={`${testId}-copy`}>
          {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5" />}
        </Button>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `hace ${s} s`;
  if (s < 3600) return `hace ${Math.round(s / 60)} min`;
  return `hace ${Math.round(s / 3600)} h`;
}

export default function TrackerSetup({ registrationId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/users/me/tracker`, { headers: { Authorization: `Bearer ${token}` } });
      setInfo(r.data);
    } catch {
      toast.error('No se pudo obtener el código de tracker');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [open, load]);

  const toggleTarget = async (regId) => {
    if (!info) return;
    const current = info.active_registrations.filter(r => r.selected).map(r => r.registration_id);
    const next = current.includes(regId) ? current.filter(id => id !== regId) : [...current, regId];
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/me/tracker/targets`, { registration_ids: next }, { headers: { Authorization: `Bearer ${token}` } });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const isLive = info?.last_seen && Date.now() - new Date(info.last_seen).getTime() < 5 * 60 * 1000;
  const active = info?.active_registrations || [];

  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="tracker-setup">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 text-left"
        data-testid="tracker-setup-toggle"
      >
        <span className="flex items-center gap-3">
          <Smartphone className="w-5 h-5 text-orange-500" />
          <span>
            <span className="block text-gray-900 font-semibold">Tracking externo (Traccar / Spotwalla)</span>
            <span className="block text-xs text-gray-500">Para navegar con otra app o GPS y seguir enviando tu posición</span>
          </span>
        </span>
        {open ? <ChevronUp className="w-6 h-6 text-gray-400" /> : <ChevronDown className="w-6 h-6 text-gray-400" />}
      </button>

      {open && (
        <div className="px-4 pb-5 space-y-5 border-t border-gray-100 pt-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Los navegadores cortan el GPS de las webs en segundo plano. Si vas a navegar con Garmin, OsmAnd, Calimoto u otra app,
            instala <strong className="text-gray-900">Traccar Client</strong> (gratuita) y configúrala <strong className="text-gray-900">una sola vez</strong> con estos datos.
            Tu código es personal y fijo: sirve para todos tus eventos.
          </p>

          <div className="flex gap-2 flex-wrap">
            <a href="https://apps.apple.com/app/traccar-client/id843156974" target="_blank" rel="noreferrer" className="text-sm px-4 py-2.5 rounded-full border border-gray-300 bg-white text-gray-800 hover:border-orange-400 hover:bg-orange-50" data-testid="traccar-ios-link">
              App Store (iPhone)
            </a>
            <a href="https://play.google.com/store/apps/details?id=org.traccar.client" target="_blank" rel="noreferrer" className="text-sm px-4 py-2.5 rounded-full border border-gray-300 bg-white text-gray-800 hover:border-orange-400 hover:bg-orange-50" data-testid="traccar-android-link">
              Google Play (Android)
            </a>
          </div>

          <div className="space-y-3">
            <CopyField label="Identificador de dispositivo (tu código personal)" value={info?.tracker_id || '…'} testId="tracker-id" />
            <CopyField label="URL del servidor" value={SERVER_URL} testId="tracker-server-url" />
          </div>

          <ol className="text-sm text-gray-600 space-y-1.5 list-decimal pl-5">
            <li>Abre Traccar Client → Ajustes y pega el <span className="text-gray-900 font-medium">identificador</span> y la <span className="text-gray-900 font-medium">URL del servidor</span> (con https).</li>
            <li>Precisión: alta. Permite ubicación "Siempre" y desactiva el ahorro de batería para la app.</li>
            <li>Activa "Seguimiento continuo" y comprueba abajo que llega tu posición.</li>
          </ol>

          <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-2" data-testid="tracker-targets">
            <p className="text-sm font-semibold text-gray-900">¿A qué evento enviar tu posición hoy?</p>
            {active.length === 0 && (
              <p className="text-xs text-gray-600">Hoy no tienes ningún evento activo. Las posiciones que envíes se descartan hasta que empiece uno.</p>
            )}
            {active.length === 1 && (
              <p className="text-xs text-gray-700" data-testid="tracker-target-single">
                Se envía automáticamente a <span className="font-semibold">{active[0].title}</span> ({active[0].start_date} → {active[0].end_date}).
              </p>
            )}
            {active.length > 1 && (
              <>
                <p className="text-xs text-gray-600">Tienes varios eventos activos. Marca los que deben recibir tu posición (puedes marcar varios, p. ej. una prueba de fin de semana que también cuenta para el anual). Si no marcas ninguno, se usa el evento más corto.</p>
                <div className="space-y-1.5">
                  {active.map(r => (
                    <label key={r.registration_id} className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer ${r.receiving ? 'border-orange-400 bg-white' : 'border-gray-200 bg-white/60'}`} data-testid={`tracker-target-${r.registration_id}`}>
                      <input type="checkbox" className="w-5 h-5 accent-orange-500" checked={r.selected} disabled={saving} onChange={() => toggleTarget(r.registration_id)} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-gray-900 font-medium truncate">{r.title}</span>
                        <span className="block text-xs text-gray-500">{r.start_date} → {r.end_date}{r.receiving && !r.selected ? ' · recibe por defecto' : ''}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 border ${isLive ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-600'}`} data-testid="tracker-status">
            <Radio className={`w-4 h-4 ${isLive ? 'text-emerald-600 animate-pulse' : 'text-gray-400'}`} />
            {info?.last_seen
              ? <>Última posición recibida {timeAgo(info.last_seen)}{info.last_source ? ` (${SOURCE_LABEL[info.last_source] || info.last_source})` : ''}</>
              : 'Aún no se ha recibido ninguna posición'}
          </div>

          {registrationId && <SpotwallaLink registrationId={registrationId} />}
        </div>
      )}
    </section>
  );
}

const SOURCE_LABEL = { app: 'App', traccar: 'Traccar', spotwalla: 'Spotwalla' };

function SpotwallaLink({ registrationId }) {
  const [info, setInfo] = useState(null);
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/registrations/${registrationId}/spotwalla`, { headers });
      setInfo(r.data);
    } catch { /* ignore */ }
  }, [registrationId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!url.trim()) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/registrations/${registrationId}/spotwalla`, { trip_url: url.trim() }, { headers });
      toast.success(`Spotwalla vinculado (${r.data.points_found} posiciones en el viaje)`);
      setUrl('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo vincular Spotwalla');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/registrations/${registrationId}/spotwalla`, { headers });
      toast.success('Enlace de Spotwalla eliminado');
      load();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3" data-testid="spotwalla-section">
      <div className="flex items-center gap-2">
        <Satellite className="w-4 h-4 text-orange-500" />
        <p className="text-gray-900 font-semibold text-sm">Spotwalla (Iron Butt)</p>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed">
        Si ya usas la app de Spotwalla, pega aquí el <span className="text-gray-900 font-medium">Trip Link</span> público de tu viaje
        (Trips → tu viaje → Links). Leeremos tus posiciones cada 2 minutos durante el evento. La frecuencia la marca tu app de Spotwalla (5-15 min).
      </p>

      {info?.trip_id ? (
        <div className="space-y-2">
          <a href={info.trip_url} target="_blank" rel="noreferrer" className="block truncate text-sm text-orange-700 font-mono underline" data-testid="spotwalla-trip-link">{info.trip_url}</a>
          <div className={`flex items-center gap-2 text-xs rounded-md px-3 py-2 border ${info.last_error ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-600'}`} data-testid="spotwalla-status">
            <Radio className="w-3.5 h-3.5" />
            {info.last_error
              ? `Error: ${info.last_error}`
              : info.last_position_at
                ? `Última posición Spotwalla ${timeAgo(info.last_position_at)}`
                : 'Vinculado. Sin posiciones todavía en este viaje.'}
          </div>
          <Button size="sm" variant="outline" onClick={remove} className="h-11 border-gray-300 bg-white hover:bg-gray-100 text-gray-700 text-sm" data-testid="spotwalla-remove-btn">
            <Unlink className="w-3.5 h-3.5 mr-1" /> Desvincular
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://spotwalla.com/trip/XXXX-XXXXXXXX-XXXX/view"
            className="flex-1 min-w-0 h-11 rounded-md bg-white border border-gray-300 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-orange-500"
            data-testid="spotwalla-url-input"
          />
          <Button onClick={save} disabled={saving || !url.trim()} className="h-11 bg-orange-500 hover:bg-orange-600 text-white shrink-0" data-testid="spotwalla-save-btn">
            {saving ? '…' : 'Vincular'}
          </Button>
        </div>
      )}
    </div>
  );
}
